import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Button,
  Drawer,
  Flex,
  Image,
  LoadingOverlay,
  Text,
  Tooltip,
} from "@mantine/core";
import { usePrevious } from "@mantine/hooks";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { createShortUrl, downloadPdf, viewPdf } from "@/app/api/screeners";
import { CONFIDENTIALITY_NOTICE } from "@/constants/email";
import { S3_URI_PREFIX } from "@/constants/s3";
import AccountMenu from "@/components/accountMenu";
import { useUserContext } from "@/context/user.context";
import {
  NotificationTypes,
  showUpwageNotification,
} from "@/utils/notifications";
import { HighlightMenu } from "@/components/highlight-menu";
import {
  ClientFitSection,
  FitSummary,
  InterviewGuide,
  KeyAnswers,
  QuestionTranscript,
  ResultsSection,
  ScreenerDetails,
  VOICE_TRANSCRIPTION_DISCLAIMER,
  VOICE_TRANSCRIPTION_UNAVAILABLE,
  VoiceAnswerLink,
} from "./sections";
import { CompetencyBadge, INFO, UpwageBadge } from "@/components/upwage-badge";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import { useCanManageClientFit } from "@/hooks/use-can-manage-client-fit";
import { useClientFitCandidateArtifactExportMutation } from "@/hooks/queries/client-fit.queries";
import {
  useCandidateScreeningsQuery,
  useHighlightScreenerSessionQuery,
  useMarkSessionViewedQuery,
} from "@/hooks/queries/screenings.queries";
import { QuestionType } from "@/types/screener-builder/questions";
import {
  ClientFitProcessingStatus,
  CompetencyFit,
  CompetencyResponseType,
  MessageSenders,
  type Screening,
} from "@/types/screeners";
import {
  ClientFitCandidateArtifactKey,
  ClientFitCandidateArtifactStatus,
  type ClientFitCandidateArtifact,
  type ClientFitCandidateArtifactExportResult,
} from "@/types/client-fit";
import type { Nullable } from "@/types/utils";
import { getCompetencyBadgeStyles } from "@/utils/competency";
import { candidateDisplayName, TestID } from "@/utils/helpers";

interface SessionResultsProps {
  email?: string;
  navigationButtons?: ReactNode;
  onClose: () => void;
  opened: boolean;
  sessionId?: string;
}

const SESSION_RESULTS_TEST_ID = new TestID("session-results", "drawer");
const DOWNLOADABLE_CLIENT_FIT_ARTIFACT_STATUSES = new Set([
  ClientFitCandidateArtifactStatus.Available,
  ClientFitCandidateArtifactStatus.Generated,
]);
const CLIENT_FIT_EXPORT_ARTIFACTS = [
  {
    key: ClientFitCandidateArtifactKey.ClientFitPdf,
    label: "Client Fit PDF",
  },
  {
    key: ClientFitCandidateArtifactKey.Resume,
    label: "resume",
  },
] as const;
const CLIENT_FIT_ARTIFACT_STATUS_LABELS: Record<
  ClientFitCandidateArtifactStatus,
  string
> = {
  [ClientFitCandidateArtifactStatus.Available]: "Available",
  [ClientFitCandidateArtifactStatus.Generated]: "Generated",
  [ClientFitCandidateArtifactStatus.Missing]: "Missing",
  [ClientFitCandidateArtifactStatus.NotReady]: "Not ready",
  [ClientFitCandidateArtifactStatus.Pending]: "Pending",
  [ClientFitCandidateArtifactStatus.Rejected]: "Rejected",
  [ClientFitCandidateArtifactStatus.Error]: "Error",
};

const getClientFitExportDisabledReason = (
  screening?: Nullable<Screening>,
): Nullable<string> => {
  if (!screening) {
    return "Select a candidate before exporting Client Fit.";
  }

  const clientFit = screening.clientFit;

  if (!clientFit) {
    return "Client Fit results are not available for this session.";
  }

  switch (clientFit.processingStatus) {
    case ClientFitProcessingStatus.New:
      return "Client Fit scoring is queued.";
    case ClientFitProcessingStatus.InProgress:
    case ClientFitProcessingStatus.Stale:
      return "Client Fit scoring is still processing.";
    case ClientFitProcessingStatus.Error:
      return "Client Fit scoring failed for this session.";
    case ClientFitProcessingStatus.Skipped:
      return "Client Fit scoring was skipped for this session.";
    case ClientFitProcessingStatus.Complete:
      return null;
    default:
      return "Client Fit scoring is not available for this session.";
  }
};

const downloadArtifact = (artifact: ClientFitCandidateArtifact) => {
  if (
    !artifact.downloadUrl ||
    !DOWNLOADABLE_CLIENT_FIT_ARTIFACT_STATUSES.has(artifact.status)
  ) {
    return false;
  }

  const link = Object.assign(document.createElement("a"), {
    download: artifact.fileName ?? "",
    href: artifact.downloadUrl,
    rel: "noreferrer",
    target: "_blank",
  });
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  return true;
};

const downloadClientFitArtifacts = (
  result: ClientFitCandidateArtifactExportResult,
) => {
  return CLIENT_FIT_EXPORT_ARTIFACTS.reduce(
    (summary, { key, label }) => {
      const artifact = result.artifacts[key];

      if (downloadArtifact(artifact)) {
        return {
          ...summary,
          downloaded: [...summary.downloaded, label],
        };
      }

      return {
        ...summary,
        unavailable: [
          ...summary.unavailable,
          `${label}: ${
            artifact.message ??
            CLIENT_FIT_ARTIFACT_STATUS_LABELS[artifact.status]
          }`,
        ],
      };
    },
    { downloaded: [] as string[], unavailable: [] as string[] },
  );
};

const formatList = (items: string[]) => {
  if (items.length <= 1) {
    return items[0] ?? "";
  }

  return `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
};

export const SessionResults = ({
  email,
  navigationButtons,
  onClose,
  opened,
  sessionId,
}: SessionResultsProps) => {
  const track = useTrackAnalytics();
  const { canSendCandidatePdfs } = useUserContext();
  const canManageClientFit = useCanManageClientFit();
  const {
    isPending: isExportingClientFitArtifacts,
    mutate: exportClientFitArtifacts,
  } = useClientFitCandidateArtifactExportMutation();
  const { mutate: mutateHighlight } = useHighlightScreenerSessionQuery();
  const { mutate: mutateSessionViewed } = useMarkSessionViewedQuery();
  const { data, isPending } = useCandidateScreeningsQuery(email, sessionId);
  const screenings = useMemo(
    () => data?.screenerSessions ?? [],
    [data?.screenerSessions],
  );
  const activeScreening = useMemo(
    () => screenings.find((screening) => screening.sessionId === sessionId),
    [screenings, sessionId],
  );
  const previousActiveScreening = usePrevious(activeScreening);
  const clientFitExportDisabledReason =
    getClientFitExportDisabledReason(activeScreening);
  const isClientFitExportDisabled =
    !activeScreening ||
    isExportingClientFitArtifacts ||
    Boolean(clientFitExportDisabledReason);
  const onViewPdfClick = useCallback(() => {
    if (activeScreening) {
      track("View PDF Clicked", {
        screener_session_id: activeScreening.sessionId,
      });

      viewPdf(activeScreening?.pdfLocation || "");
    }
  }, [activeScreening, track]);

  const onDownloadPdfClick = useCallback(async () => {
    if (!activeScreening?.pdfLocation) {
      return;
    }

    track("Download PDF Clicked", {
      screener_session_id: activeScreening.sessionId,
    });

    try {
      await downloadPdf(activeScreening.pdfLocation);
    } catch (error) {
      console.error("Failed to download PDF:", error);
      showUpwageNotification(
        "Failed to download PDF. Please try again.",
        NotificationTypes.Error,
      );
    }
  }, [activeScreening, track]);

  const [isSharingPdf, setIsSharingPdf] = useState(false);

  const onExportClientFitArtifactsClick = useCallback(() => {
    if (!activeScreening || getClientFitExportDisabledReason(activeScreening)) {
      return;
    }

    track("Client Fit Artifacts Exported", {
      screener_session_id: activeScreening.sessionId,
      selected_count: 1,
    });

    exportClientFitArtifacts(
      {
        generateMissingClientFitPdf: true,
        sessionIds: [activeScreening.sessionId],
      },
      {
        onError: (error) => {
          console.error("Failed to export Client Fit artifacts:", error);
          showUpwageNotification(
            "Failed to export Client Fit artifacts. Please try again.",
            NotificationTypes.Error,
          );
        },
        onSuccess: (response) => {
          const result = response.results.find(
            (result) => result.sessionId === activeScreening.sessionId,
          );

          if (!result) {
            showUpwageNotification(
              "Client Fit artifacts were not returned for this session.",
              NotificationTypes.Error,
            );
            return;
          }

          const { downloaded, unavailable } =
            downloadClientFitArtifacts(result);

          if (downloaded.length > 0) {
            showUpwageNotification(
              `${formatList(downloaded)} download${
                downloaded.length === 1 ? "" : "s"
              } started.${
                unavailable.length > 0
                  ? ` ${formatList(unavailable)} unavailable.`
                  : ""
              }`,
              unavailable.length > 0
                ? NotificationTypes.Info
                : NotificationTypes.Success,
            );
            return;
          }

          showUpwageNotification(
            `No Client Fit artifacts are ready to download. ${formatList(
              unavailable,
            )}.`,
            NotificationTypes.Error,
          );
        },
      },
    );
  }, [activeScreening, exportClientFitArtifacts, track]);

  const onSharePdfClick = useCallback(async () => {
    if (!activeScreening?.pdfLocation) {
      return;
    }

    track("Share PDF Clicked", {
      screener_session_id: activeScreening.sessionId,
    });

    setIsSharingPdf(true);
    try {
      const pdfUrl = await createShortUrl(
        activeScreening.pdfLocation,
        activeScreening.candidateName ?? undefined,
      );
      const subject = `Screening Results - ${candidateDisplayName(activeScreening.candidateName)} - ${activeScreening.jobTitle}`;
      const body = [
        "Hi,",
        "",
        `Please find the screening results for ${candidateDisplayName(activeScreening.candidateName)} (${activeScreening.jobTitle} - ${activeScreening.locationName}).`,
        "",
        `View PDF: ${pdfUrl}`,
        "",
        "Note: This link remains active while screening data exists.",
        "",
        "Best regards",
        "",
        "---",
        "",
        CONFIDENTIALITY_NOTICE,
      ].join("\n");

      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    } catch (error) {
      console.error("Failed to get PDF URL for email sharing:", error);
      showUpwageNotification(
        "Failed to prepare PDF email. Please try again.",
        NotificationTypes.Error,
      );
    } finally {
      setIsSharingPdf(false);
    }
  }, [activeScreening, track]);

  const clientFitExportButton = (
    <Button
      data-testid={
        SESSION_RESULTS_TEST_ID.addComponent({
          element: "export-client-fit-artifacts",
        }).id
      }
      disabled={isClientFitExportDisabled}
      loading={isExportingClientFitArtifacts}
      onClick={onExportClientFitArtifactsClick}
      rightSection={<FontAwesomeIcon icon={faDownload} />}
      size="sm"
      variant="outline"
    >
      Export Client Fit
    </Button>
  );

  const handleHighlight = async (value: Nullable<string>) => {
    if (activeScreening) {
      track("Candidate Highlighted", {
        highlight_color: value || "None",
        screener_session_id: activeScreening.sessionId,
      });

      mutateHighlight({ id: activeScreening.sessionId, value });
    }
  };

  useEffect(() => {
    const currentSessionIsUnviewed =
      activeScreening &&
      !activeScreening.viewedAt &&
      activeScreening.sessionId !== previousActiveScreening?.sessionId;
    const currentSessionIsIncomplete = !!sessionId && !email;

    // This will fire on every render for sessions without an email. As there
    // is nothing the user can actually do or interact with when there is no
    // email this shouldn't be a problem. This is due to needing to rely on
    // email for the query, without a provided email we have no way to know
    // whether the session has been viewed or not.
    if (currentSessionIsUnviewed || currentSessionIsIncomplete) {
      // Safe to cast, TypeScript is just losing the thread. As long as one of
      // the above is true the following will always result in a string.
      mutateSessionViewed((activeScreening?.sessionId ?? sessionId) as string);
    }
  }, [
    activeScreening,
    email,
    mutateSessionViewed,
    previousActiveScreening,
    sessionId,
  ]);

  return (
    <Drawer
      classNames={{
        body: "flex flex-col grow p-0 max-w-full",
        content: "flex",
      }}
      onClose={onClose}
      opened={opened}
      overlayProps={{ backgroundOpacity: 0.1, blur: 0 }}
      position="right"
      shadow="xl"
      size="lg"
      withCloseButton={false}
    >
      <LoadingOverlay visible={Boolean(email && isPending)} />

      <Flex align="center" className="p-4 w-full" justify="space-between">
        <Image src="/assets/logo.svg" alt="Logo" h={20} />
        <ActionIcon
          aria-label="close"
          data-testid={
            SESSION_RESULTS_TEST_ID.addComponent({
              element: "close",
            }).id
          }
          onClick={onClose}
          variant="transparent"
        >
          <Image src="/assets/circle-x.svg" alt="Logo" h={20} />
        </ActionIcon>
      </Flex>

      <Flex className="border-b border-t p-4">
        <Flex className="gap-2">
          <Button
            data-testid={
              SESSION_RESULTS_TEST_ID.addComponent({
                element: "view-pdf",
              }).id
            }
            disabled={!activeScreening?.pdfLocation}
            onClick={onViewPdfClick}
            rightSection={<FontAwesomeIcon icon={faEye} />}
            size="sm"
          >
            View PDF
          </Button>
          <Button
            data-testid={
              SESSION_RESULTS_TEST_ID.addComponent({
                element: "download-pdf",
              }).id
            }
            disabled={!activeScreening?.pdfLocation}
            onClick={onDownloadPdfClick}
            rightSection={<FontAwesomeIcon icon={faDownload} />}
            size="sm"
            variant="outline"
          >
            Download PDF
          </Button>
          {canSendCandidatePdfs && (
            <Button
              data-testid={
                SESSION_RESULTS_TEST_ID.addComponent({
                  element: "share-pdf",
                }).id
              }
              disabled={!activeScreening?.pdfLocation || isSharingPdf}
              loading={isSharingPdf}
              onClick={onSharePdfClick}
              rightSection={<FontAwesomeIcon icon={faEnvelope} />}
              size="sm"
              variant="outline"
            >
              Share PDF
            </Button>
          )}
          {canManageClientFit &&
            (clientFitExportDisabledReason ? (
              <Tooltip
                label={clientFitExportDisabledReason}
                multiline
                w={240}
                withArrow
              >
                <span>{clientFitExportButton}</span>
              </Tooltip>
            ) : (
              clientFitExportButton
            ))}
          <HighlightMenu
            activeIndicatorColor={activeScreening?.highlightColor}
            onChange={handleHighlight}
            disabled={!activeScreening}
          />
        </Flex>
        <AccountMenu />
      </Flex>

      {!email && (
        <Flex align="center" className="grow w-full" justify="center">
          <Text>Incomplete Interview Session</Text>
        </Flex>
      )}

      {activeScreening && (
        <Flex className="gap-2 px-4 py-3" direction="column">
          <Flex direction="column">
            <Flex align="center" className="w-full" justify="space-between">
              <Text className="font-semibold" size="xl">
                {candidateDisplayName(activeScreening.candidateName)}
              </Text>
              {activeScreening.screenerVersionNumber && (
                <UpwageBadge colorVariant={INFO} filled>
                  {`V${activeScreening.screenerVersionNumber}`}
                </UpwageBadge>
              )}
            </Flex>
            <Text className="font-semibold" size="sm">
              {activeScreening.candidateEmail} |{" "}
              {activeScreening.candidatePhone}
            </Text>
          </Flex>

          <ScreenerDetails screening={activeScreening} />

          <FitSummary screening={activeScreening} />

          {canManageClientFit && (
            <ClientFitSection screening={activeScreening} />
          )}

          <KeyAnswers screening={activeScreening} />
          <InterviewGuide screening={activeScreening} />

          {activeScreening.overallFit &&
            activeScreening.overallFit !== CompetencyFit.NotAsked && (
              <ResultsSection
                style={{
                  // Background of the overall fit section matches the badge
                  // background, but with lightness set to 95%
                  backgroundColor: `hsl(from ${
                    getCompetencyBadgeStyles(activeScreening.overallFit, true)
                      .background
                  } h s 95%)`,
                }}
                title="Overall fit"
                titleBadge={
                  <CompetencyBadge
                    isOverallFit
                    score={activeScreening.overallFit}
                  />
                }
              >
                <Text c="gray.9" size="sm">
                  Overall fit combines all the competencies below. Competencies
                  are set by company leaders to predict retention, performance,
                  and fit. We welcome feedback on competencies and encourage you
                  to dive into the quotes that speak to each one.
                </Text>
              </ResultsSection>
            )}

          {activeScreening.competencies &&
            map(
              activeScreening.competencies,
              (
                {
                  audioPlaybackUrl,
                  quote,
                  responseType,
                  score,
                  transcriptionText,
                  transcriptionFailureReason,
                },
                key,
              ) => (
                <ResultsSection
                  key={key}
                  title={key}
                  titleBadge={
                    <Flex align="center" gap="xs">
                      {responseType === CompetencyResponseType.Voice && (
                        <UpwageBadge colorVariant={INFO}>
                          Voice Response
                        </UpwageBadge>
                      )}
                      {score && <CompetencyBadge score={score} />}
                    </Flex>
                  }
                >
                  <Text c="gray.9" size="sm">
                    {quote || "No available quote for this competency."}
                  </Text>
                  {responseType === CompetencyResponseType.Voice && (
                    <Flex className="mt-2" direction="column">
                      {audioPlaybackUrl && (
                        <VoiceAnswerLink
                          href={audioPlaybackUrl}
                          label="PLAY RECORDING"
                          sessionId={activeScreening.sessionId}
                        />
                      )}
                      {transcriptionText && (
                        <Text c="gray.5" size="xs">
                          {VOICE_TRANSCRIPTION_DISCLAIMER}
                        </Text>
                      )}
                      {/* AS-2044: terminal failure reads as final, not
                          pending. The sweep no longer retries these rows. */}
                      {!transcriptionText && transcriptionFailureReason && (
                        <Text
                          c="gray.5"
                          className="italic"
                          size="xs"
                          title={transcriptionFailureReason}
                        >
                          {VOICE_TRANSCRIPTION_UNAVAILABLE}
                        </Text>
                      )}
                      {!transcriptionText &&
                        !transcriptionFailureReason &&
                        audioPlaybackUrl && (
                          <Text c="gray.5" className="italic" size="xs">
                            Transcription pending...
                          </Text>
                        )}
                    </Flex>
                  )}
                </ResultsSection>
              ),
            )}

          {activeScreening.basicQuestions &&
            activeScreening.basicQuestions.length > 0 && (
              <QuestionTranscript
                fileContext={{
                  candidateName: activeScreening.candidateName,
                  createdAt: activeScreening.createdAt,
                  jobTitle: activeScreening.jobTitle,
                }}
                sessionId={activeScreening.sessionId}
                title="Basic Questions"
                transcript={orderBy(
                  activeScreening.basicQuestions,
                  "questionOrder",
                ).map(
                  ({
                    inputType,
                    internalLabel,
                    message,
                    qualifyingPassed,
                    questionRootId,
                    sender,
                  }) => ({
                    internalLabel,
                    isFileResponse:
                      inputType === QuestionType.FileUpload &&
                      sender === MessageSenders.Candidate,
                    message,
                    name: sender,
                    questionRootId,
                    rightSection:
                      qualifyingPassed === null ? null : (
                        <UpwageBadge
                          colorVariant={
                            qualifyingPassed
                              ? CompetencyFit.VeryHigh
                              : CompetencyFit.VeryLow
                          }
                        >
                          {qualifyingPassed ? "PASSED" : "FAILED"}
                        </UpwageBadge>
                      ),
                  }),
                )}
              />
            )}
          {/*
            Voice Questions section: dedupes against Assessment Questions
            for 3.0 sessions. Santi flagged the duplication on the AS-1844
            cluster — voice ASSESSMENT rows on 3.0 are already inlined
            under Assessment Questions via the SRN-715 mirror, so showing
            them here too is redundant. But the API splits voice across
            three arrays (Justen review on PR #3453):
              - basicQuestions:      type=basic AND input_type!=voice
              - assessmentQuestions: type=assessment (any input_type)
              - voiceQuestions:      input_type=voice (any type)
            So 3.0 BASIC voice questions (basic + input_type=voice) only
            live in voiceQuestions. Gating the whole section off for
            is3PointZero hides them entirely. Instead, dedupe by
            questionId: render rows whose questionId isn't already
            represented in assessmentQuestions. 2.0 keeps the legacy
            behavior — render everything in voiceQuestions.
            Mirrors the PR #3426 gating on the PDF side.
          */}
          {(() => {
            const voiceQuestions = activeScreening.voiceQuestions ?? [];
            if (voiceQuestions.length === 0) return null;

            const visibleVoiceQuestions = activeScreening.is3PointZero
              ? (() => {
                  // Cursor review on this PR — the API keys the two arrays
                  // differently:
                  //   voiceQuestions[i].questionId  = question_root_id ||
                  //                                   screener_question_id
                  //   assessmentQuestions[i].questionId     = question_id (instance)
                  //   assessmentQuestions[i].questionRootId = question_root_id
                  // On a mirrored 3.0 assessment voice row, the voice
                  // entry carries the root id and the assessment entry
                  // carries the instance id, so a single-key dedupe never
                  // matches and the duplication Santi flagged comes
                  // back. Build the set from both keys on the assessment
                  // side; matching either suppresses the voice entry.
                  const assessmentIds = new Set<string>();
                  for (const q of activeScreening.assessmentQuestions ?? []) {
                    if (q.questionId) assessmentIds.add(q.questionId);
                    const rootId = (q as { questionRootId?: string })
                      .questionRootId;
                    if (rootId) assessmentIds.add(rootId);
                  }
                  return voiceQuestions.filter(
                    (q) => !assessmentIds.has(q.questionId),
                  );
                })()
              : voiceQuestions;

            if (visibleVoiceQuestions.length === 0) return null;

            return (
              <QuestionTranscript
                sessionId={activeScreening.sessionId}
                title="Voice Questions"
                transcript={visibleVoiceQuestions.map(({ message, sender }) => {
                  const isCandidateVoice =
                    sender === MessageSenders.Candidate && message !== "Skip";
                  // 2.0: the API presigns the S3 key into an HTTPS URL and
                  // returns it as `message` (see extractVoiceQuestions in
                  // screener_session.service.ts). QuestionTranscript only
                  // renders playback when audioPlaybackUrl is set or
                  // message starts with s3://, so plumb the presigned URL
                  // through as audioPlaybackUrl. Before AS-1844 follow-up,
                  // key-answers.tsx rendered playback by reading `message`
                  // directly into VoiceAnswerLink; dropping that block
                  // moved 2.0 playback into this section, which needs the
                  // URL on a field QuestionTranscript actually consumes.
                  return {
                    audioPlaybackUrl: isCandidateVoice ? message : undefined,
                    isVoiceResponse: isCandidateVoice,
                    message,
                    name: sender,
                  };
                })}
              />
            );
          })()}
          {activeScreening.assessmentQuestions &&
            activeScreening.assessmentQuestions.length > 0 && (
              <QuestionTranscript
                sessionId={activeScreening.sessionId}
                title="Assessment Questions"
                transcript={(() => {
                  // Render candidate voice answers as plain transcript
                  // text — recording playback lives in the competency
                  // section beside the relevant quote, not here.
                  //
                  // Build a questionId → transcriptionText map by
                  // walking voice competencies positionally against the
                  // distinct voice candidate rows (s3:// or empty
                  // message). Same pairing as SRN-720 but only the
                  // transcript text is needed.
                  const transcriptByQuestionId = new Map<string, string>();
                  // AS-2044: paired positionally the same way the transcript is,
                  // so a permanently-failed voice answer reads as failed in the
                  // Assessment Questions transcript too, not only beside the quote.
                  const failureByQuestionId = new Map<string, string>();

                  if (activeScreening.competencies) {
                    const voiceCompetencies = Object.values(
                      activeScreening.competencies,
                    ).filter(
                      (c) => c.responseType === CompetencyResponseType.Voice,
                    );

                    // AS-2044 (review): join on the audio key, not on position.
                    //
                    // The old pairing walked voice competencies in order against candidate
                    // rows in order. Whenever those two orders differ it attaches one
                    // question's result to another — tolerable-ish when the only casualty
                    // was a transcript, not tolerable now: a mis-paired FAILURE reads
                    // "could not be transcribed" under an answer that transcribed fine and
                    // hides the real transcript.
                    //
                    // Positional matching survives only as the fallback for rows that carry
                    // NO key (legacy 2.0, whose message is empty). A row that names a key
                    // matching nothing is left unmapped and renders pending — an honest
                    // "not yet" beats a confident wrong answer, same as the PDF.
                    // Formats differ across the codebase: a row's message is a full
                    // `s3://bucket/path` URI, while a competency's audioS3Key may be either
                    // that or the bare path (hence `normalizeS3Key` server-side). Comparing
                    // raw strings would miss every time the two disagree and render EVERY
                    // answer pending — worse than the mis-pairing this fixes.
                    const norm = (k: string) =>
                      k.replace(/^s3:\/\/[^/]+\//, "");
                    const byAudioKey = new Map<
                      string,
                      (typeof voiceCompetencies)[number]
                    >();
                    for (const vc of voiceCompetencies) {
                      if (vc.audioS3Key)
                        byAudioKey.set(norm(vc.audioS3Key), vc);
                    }
                    // Only REFUSE the positional fallback when key data actually exists to
                    // match against. Competencies predating this field, and the 2.0 path,
                    // carry no key at all — being strict there would blank answers that
                    // pair correctly today for no gain.
                    const haveKeyData = byAudioKey.size > 0;

                    const used = new Set<(typeof voiceCompetencies)[number]>();
                    const seen = new Set<string>();
                    for (const q of activeScreening.assessmentQuestions) {
                      if (
                        q.sender === MessageSenders.Candidate &&
                        !seen.has(q.questionId) &&
                        (q.message === "" || q.message.startsWith("s3://"))
                      ) {
                        seen.add(q.questionId);
                        const audioKey = q.message.startsWith("s3://")
                          ? q.message
                          : undefined;
                        const keyed = audioKey
                          ? byAudioKey.get(norm(audioKey))
                          : undefined;
                        const vc =
                          keyed && !used.has(keyed)
                            ? keyed
                            : audioKey && haveKeyData
                              ? undefined
                              : voiceCompetencies.find((c) => !used.has(c));
                        if (!vc) continue;
                        used.add(vc);
                        if (vc.transcriptionText) {
                          transcriptByQuestionId.set(
                            q.questionId,
                            vc.transcriptionText,
                          );
                        } else if (vc.transcriptionFailureReason) {
                          failureByQuestionId.set(
                            q.questionId,
                            vc.transcriptionFailureReason,
                          );
                        }
                      }
                    }
                  }

                  return activeScreening.assessmentQuestions.map(
                    ({ message, questionId, sender }) => {
                      // Candidate voice rows: render the transcript as
                      // plain text. Voice-ness is detected by:
                      //   - empty message: 2.0 voice candidate, transcript
                      //     in the matched competency
                      //   - s3:// message: 3.0 voice candidate, transcript
                      //     also in the matched competency (or pending
                      //     if Whisper hasn't landed yet)
                      const isVoiceCandidate =
                        sender === MessageSenders.Candidate &&
                        (message === "" || message.startsWith(S3_URI_PREFIX));

                      if (isVoiceCandidate) {
                        const transcript =
                          transcriptByQuestionId.get(questionId);
                        // AS-2044: this block renders the transcript as plain
                        // `message`, so the terminal state is a different
                        // fallback string rather than a separate branch. A
                        // permanently-failed answer must not read as pending —
                        // since AS-2043 the sweep never retries it, so "pending"
                        // would be a promise nothing will keep.
                        const failed = failureByQuestionId.get(questionId);
                        return {
                          message:
                            transcript ??
                            (failed
                              ? VOICE_TRANSCRIPTION_UNAVAILABLE
                              : "Transcription pending..."),
                          name: sender,
                        };
                      }

                      // Non-voice rows (text answers, Skip, screener
                      // prompts) pass through unchanged.
                      return { message, name: sender };
                    },
                  );
                })()}
              />
            )}
        </Flex>
      )}

      {navigationButtons}
    </Drawer>
  );
};
